import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { CidaasUser } from '../../../../feature/user-administration/model/cidaas-user';
import { UserGroup } from '../../../../feature/user-management/user-group-management/model/user-group';
import { CreateUserRequest } from '../../../model/user/create-user.interface';
import { UserDirectoryCompany as Company } from '../../../model/user/user-directory-company';
import { UserDirectoryUser } from '../../../model/user/user-directory-user';
import { IdentityService } from '../../../service/identity.service';
import { NotificationService } from '../../../service/notification.service';
import { UserDirectoryCompanyTableComponent } from '../user-directory-company-table/user-directory-company-table.component';

@Component({
  selector: 'gate-user-directory-import-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ButtonModule,
    DialogModule,
    UserDirectoryCompanyTableComponent,
  ],
  templateUrl: './user-directory-import-dialog.component.html',
})
export class UserDirectoryImportDialogComponent {
  private identityService = inject(IdentityService);
  private notificationService = inject(NotificationService);

  @Input() visible = false;
  @Input() user: UserDirectoryUser | null = null;
  @Input() companies: Company[] = [];
  @Input() isModuleAdmin = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() imported = new EventEmitter<Company[]>();

  public isImporting = false;
  public companySelected: Record<string, boolean> = {};
  public companySelectedGroups: Record<string, UserGroup[]> = {};

  public closeDialog(): void {
    if (this.isImporting) {
      return;
    }
    this.resetSelection();
    this.visibleChange.emit(false);
  }

  public onCompanyCheckChange(company: Company): void {
    const isSelected = this.companySelected[company.rs2CompanyNo];
    const groups = company.securityGroups ?? [];
    if (isSelected && groups.length === 1) {
      this.companySelectedGroups[company.rs2CompanyNo] = [...groups];
    }
    if (!isSelected) {
      delete this.companySelectedGroups[company.rs2CompanyNo];
    }
  }

  public areAllCompaniesSelected(): boolean {
    if (this.companies.length === 0) {
      return false;
    }
    return this.companies.every((company) => !!this.companySelected[company.rs2CompanyNo]);
  }

  public toggleAllCompanies(checked: boolean): void {
    this.companies.forEach((company) => {
      this.companySelected[company.rs2CompanyNo] = checked;
      const groups = company.securityGroups ?? [];
      if (checked && groups.length === 1) {
        this.companySelectedGroups[company.rs2CompanyNo] = [...groups];
      }
      if (!checked) {
        delete this.companySelectedGroups[company.rs2CompanyNo];
      }
    });
  }

  public onGroupSelectionChange(company: Company, selected: UserGroup[]): void {
    this.companySelectedGroups[company.rs2CompanyNo] = selected ?? [];
  }

  public selectedCompanyCount(): number {
    return this.getSelectedCompanies().length;
  }

  public async applyCompany(company: Company): Promise<void> {
    this.companySelected = {};
    this.companySelected[company.rs2CompanyNo] = true;
    const groups = company.securityGroups ?? [];
    if (!this.companySelectedGroups[company.rs2CompanyNo] && groups.length === 1) {
      this.companySelectedGroups[company.rs2CompanyNo] = [...groups];
    }
    await this.importSelectedCompanies();
  }

  public canImportUser(): boolean {
    const selected = this.getSelectedCompanies();
    if (selected.length === 0) {
      return false;
    }
    return selected.every((company) => (this.companySelectedGroups[company.rs2CompanyNo] ?? []).length > 0);
  }

  public async importSelectedCompanies(): Promise<void> {
    const cidaasUser = this.user?.payload.cidaas;
    if (!this.user || !cidaasUser) {
      this.notificationService.showNotification(
        'error',
        'notification.userOnboarding.import.error.title',
        'notification.userOnboarding.import.error.message',
      );
      return;
    }

    const selectedCompanies = this.getSelectedCompanies();
    if (selectedCompanies.length === 0) {
      this.notificationService.showNotification(
        'error',
        'notification.userOnboarding.import.error.title',
        'userAdministration.error.noCompanySelected',
      );
      return;
    }

    const companiesWithoutGroups = selectedCompanies.filter(
      (company) => (this.companySelectedGroups[company.rs2CompanyNo] ?? []).length === 0,
    );
    if (companiesWithoutGroups.length > 0) {
      this.notificationService.showNotification(
        'error',
        'notification.userImport.error.noGroup',
        'notification.userImport.selectGroupBeforeImport',
      );
      return;
    }

    this.isImporting = true;
    const payloads = selectedCompanies.map((company) => this.buildCreateUserRequest(cidaasUser, company));

    try {
      const result = await this.identityService.createUser(payloads);
      const successCount = result?.length ?? 0;

      if (successCount === payloads.length) {
        this.notificationService.showNotification(
          'success',
          'notification.userOnboarding.import.success.title',
          'notification.userOnboarding.import.success.message',
        );
      } else if (successCount > 0) {
        this.notificationService.showNotification(
          'success',
          'notification.userOnboarding.import.partialSuccess.title',
          'notification.userOnboarding.import.partialSuccess.message',
        );
      } else {
        this.notificationService.showNotification(
          'error',
          'notification.userOnboarding.import.error.title',
          'notification.userOnboarding.import.error.message',
        );
      }

      if (successCount > 0 && cidaasUser.cidaasSub) {
        this.identityService.clearUserHierarchyCache(cidaasUser.cidaasSub);
        const successfulCompanyNos = new Set(
          (result ?? [])
            .map((createdUser: any) => createdUser?.customerId)
            .filter((customerId: string | undefined): customerId is string => !!customerId),
        );
        const importedCompanies = successfulCompanyNos.size > 0
          ? selectedCompanies.filter((company) => successfulCompanyNos.has(company.rs2CompanyNo))
          : selectedCompanies.slice(0, successCount);
        this.imported.emit(importedCompanies);
      }
      this.resetSelection();
      this.visibleChange.emit(false);
    } catch (error) {
      console.error('Import error:', error);
      this.notificationService.showNotification(
        'error',
        'notification.userOnboarding.import.error.title',
        'notification.userOnboarding.import.error.message',
      );
    } finally {
      this.isImporting = false;
    }
  }

  private getSelectedCompanies(): Company[] {
    return this.companies.filter((company) => this.companySelected[company.rs2CompanyNo]);
  }

  private resetSelection(): void {
    this.companySelected = {};
    this.companySelectedGroups = {};
  }

  private buildCreateUserRequest(cidaasUser: CidaasUser, company: Company): CreateUserRequest {
    const normalizedSalutation = cidaasUser.salutation?.toLowerCase().replace('.', '').trim();
    const gender = normalizedSalutation
      ? ['hr', 'mr', 'herr'].includes(normalizedSalutation)
        ? 'Herr'
        : ['fr', 'ms', 'frau'].includes(normalizedSalutation)
          ? 'Frau'
          : undefined
      : undefined;

    return {
      userEntity: {
        gender,
        given_name: cidaasUser.firstName,
        family_name: cidaasUser.lastName,
        email: cidaasUser.eMail,
        roles: [],
        groups: [
          {
            groupId: company.rs2CompanyNo,
            appendRole: true,
          },
        ],
      },
      securityGroupIds: (this.companySelectedGroups[company.rs2CompanyNo] ?? []).map((group) => group.id),
    } as CreateUserRequest;
  }
}
